import { useState, useEffect } from 'react'
import { 
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts'
import { getSeasonalSalesData } from '../../supabase-client'

// Custom tooltip with shadcn styling
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                Total Volume
              </span>
            </div>
            <span className="font-medium">
              {entry.value?.toFixed(1)} kg
            </span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

export default function SeasonalDemandRadarChart({ loading = false }) {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      if (loading) return
      
      try {
        setIsLoading(true)
        setError(null)
        const seasonalData = await getSeasonalSalesData()
        
        // Transform data to use shorter month names for better display
        const chartData = seasonalData.map(item => ({
          ...item,
          month: item.month.substring(0, 3) // Jan, Feb, Mar, etc.
        }))
        
        console.log('Seasonal radar chart data:', chartData)
        setData(chartData)
      } catch (err) {
        console.error('Error fetching seasonal data:', err)
        setError('Failed to load seasonal data')
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [loading])

  if (loading || isLoading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading seasonal demand data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="text-sm text-destructive">{error}</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="text-sm text-muted-foreground">No seasonal data available</div>
      </div>
    )
  }

  return (
    <div className="w-full h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
          <PolarGrid stroke="hsl(var(--border))" strokeWidth={1} />
          <PolarAngleAxis 
            dataKey="month" 
            tick={{ fill: 'hsl(var(--foreground))', fontSize: 12 }}
          />
          <PolarRadiusAxis 
            angle={90}
            domain={[0, 'dataMax']}
            tickCount={5}
            tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
            tickFormatter={(value) => `${value.toFixed(0)}kg`}
          />
          <Radar
            name="Volume (kg)"
            dataKey="volume"
            stroke="#009689"
            fill="#009689"
            fillOpacity={0.2}
            strokeWidth={2}
            dot={{ fill: '#009689', strokeWidth: 2, r: 3 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            wrapperStyle={{ 
              paddingTop: '20px',
              color: 'hsl(var(--foreground))',
              fontSize: '12px'
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  )
}